import { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  FileSearch,
  Zap,
  ListTodo,
  Save,
  X,
  Hash,
  Fuel,
  MapPin,
  Building,
  User,
  Mail,
  Phone,
  CreditCard,
  Package,
  Pencil,
  Trash2,
  ExternalLink,
  AlertCircle,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { Button, Badge, Spinner, useToast, cn, ConfirmDialog } from '@centraoneplus/ui';
import { format } from 'date-fns';
import { apiClient } from '@services/api-client';
import { ticketTypesService } from '@services/ticket-types.service';
import { getFieldKind, validateFieldValue, htmlInputType } from './fieldValidation';
import { sanitizePhoneInput } from '@/lib/phone';

// Types
interface LineItemDetails {
  [key: string]: any;
}

interface LineItemSource {
  type: string;
  primaryIdentifier: string;
  primaryValue: string;
  confidence: number;
  extractedAt: string;
}

interface LineItem {
  id: string;
  ticketId: string;
  content: string;
  contentHtml?: string;
  noteType: 'line_item';
  lineItemDetails: LineItemDetails;
  lineItemStatus: 'pending' | 'in_progress' | 'completed';
  lineItemDueDate?: string;
  lineItemAssigneeId?: string;
  lineItemSortOrder?: number;
  lineItemSource?: LineItemSource;
  createdAt: string;
  updatedAt: string;
}

interface LineItemStats {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
}

interface LineItemsTabProps {
  ticketId: string;
  compact?: boolean;
  ticketCustomFields?: Record<string, any>;
  /**
   * A second line under a field's value, keyed by field key: an English
   * rendering of what the customer wrote, or - for a date the assistant
   * resolved to a timestamp - the phrasing they actually used. Absent for an
   * English conversation, which is most of them.
   */
  fieldNotes?: Record<string, string>;
  ticketTypeName?: string; // Used to conditionally show quote buttons (only for "Quotation" type)
  ticketTypeId?: string; // Drives which fields the line item shows (current type's fields only)
}

/** A custom field defined by the current ticket type (drives line-item display). */
interface SchemaField {
  key: string;
  name: string;
  required?: boolean; // From the ticket type's field config (isRequired) — enforced on save.
  type?: string; // fieldType from the schema (date/url/email/number/…) — drives input type + validation.
}

// API Service
export const lineItemsService = {
  async getLineItems(ticketId: string): Promise<LineItem[]> {
    const response = await apiClient.get(`/notes/ticket/${ticketId}/line-items`);
    return response.data;
  },

  async getStats(ticketId: string): Promise<LineItemStats> {
    const response = await apiClient.get(`/notes/ticket/${ticketId}/line-items/stats`);
    return response.data;
  },

  async updateLineItem(id: string, data: Partial<LineItem>): Promise<LineItem> {
    const response = await apiClient.put(`/notes/line-items/${id}`, data);
    return response.data;
  },

  async updateStatus(
    id: string,
    status: 'pending' | 'in_progress' | 'completed',
  ): Promise<LineItem> {
    // Dedicated status route — records a "Line item marked …" history entry.
    const response = await apiClient.put(`/notes/line-items/${id}/status`, { status });
    return response.data;
  },

  async createLineItem(
    ticketId: string,
    details: Record<string, any>,
    content?: string,
  ): Promise<LineItem> {
    const response = await apiClient.post(`/notes/ticket/${ticketId}/line-items`, {
      details,
      content,
    });
    return response.data;
  },

  async deleteLineItem(id: string): Promise<void> {
    await apiClient.delete(`/notes/line-items/${id}`);
  },

  async generateQuote(lineItemId: string): Promise<{
    success: boolean;
    quotationLink?: string;
    brokerLink?: string;
    error?: string;
  }> {
    const response = await apiClient.post(`/notes/line-items/${lineItemId}/generate-quote`);
    return response.data;
  },
};

// Most-recently-updated line item first — the same order the server returns
// (notes.repository.findLineItems). Re-applied locally after every mutation
// because those replace the item in place, which would otherwise leave the
// list in its pre-edit order until the next refresh.
const byUpdatedAtDesc = (items: LineItem[]): LineItem[] =>
  [...items].sort((a, b) => {
    const diff = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    // Same instant (e.g. a batch extracted from one email) — fall back to
    // createdAt so the order stays stable instead of shuffling between renders.
    return diff !== 0 ? diff : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

// Format field key to readable label
const formatFieldLabel = (key: string): string => {
  const labelMap: Record<string, string> = {
    mpan: 'MPAN',
    mprn: 'MPRN',
    eac: 'EAC (kWh)',
    aq: 'AQ',
    start_date: 'Start Date',
    end_date: 'End Date',
    fuel_type: 'Fuel Type',
    contract_length: 'Contract Length',
    supplier: 'Supplier',
    customer_name: 'Customer Name',
    site_address: 'Site Address',
    postcode: 'Postcode',
    brokerEmail: 'Broker Email',
    brokeremail: 'Broker Email',
    brokerLink: 'Broker Portal',
    brokerlink: 'Broker Portal',
    quotationLink: 'Quote Link',
    quotationlink: 'Quote Link',
    horizonStatus: 'Quote Status',
    horizonstatus: 'Quote Status',
  };

  if (labelMap[key.toLowerCase()]) {
    return labelMap[key.toLowerCase()];
  }

  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

// Get icon for field type
const getFieldIcon = (key: string) => {
  const lowerKey = key.toLowerCase();
  if (lowerKey.includes('mpan') || lowerKey.includes('mprn')) return Hash;
  if (lowerKey.includes('date')) return Calendar;
  if (lowerKey.includes('fuel')) return Fuel;
  if (lowerKey.includes('eac') || lowerKey.includes('aq')) return Zap;
  if (lowerKey.includes('address') || lowerKey.includes('site')) return MapPin;
  if (lowerKey.includes('postcode')) return MapPin;
  if (lowerKey.includes('supplier') || lowerKey.includes('company')) return Building;
  if (lowerKey.includes('customer') || lowerKey.includes('name')) return User;
  if (lowerKey.includes('email')) return Mail;
  if (lowerKey.includes('phone')) return Phone;
  if (lowerKey.includes('contract')) return CreditCard;
  if (lowerKey.includes('link') || lowerKey.includes('url')) return ExternalLink;
  if (lowerKey.includes('status') && lowerKey.includes('horizon')) return AlertCircle;
  return Package;
};

// Check if field is a link field
const isLinkField = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  return lowerKey === 'brokerlink' || lowerKey === 'quotationlink';
};

// Reserved detail key recording which ticket type a line item's values were
// entered under. A value only displays when this matches the currently-selected
// type, so a field shared by two types (e.g. "Created Date") shows its value
// only for its owning type and reads blank under any other. Internal — never
// rendered as a field.
const TYPE_OWNER_KEY = '__ticketTypeId';

// The line item's name is a built-in property, not one of the ticket type's
// custom fields, so it has no field key to hang a validation error off. This
// sentinel lets it share the card's existing `fieldErrors` map (and therefore
// the summary banner and the cancel/reset path) without a parallel state.
// `__`-prefixed keys are reserved for internals, so it can't collide with a
// configured field key.
const NAME_ERROR_KEY = '__name';

// Fields to hide from regular display (internal quote status/links, shown
// separately or surfaced in the ticket History — never as a line-item field).
const hiddenFields = [
  'horizonstatus',
  'horizonerror',
  'externalapistatus',
  'externalapierror',
  'brokerlink',
  'quotationlink',
  TYPE_OWNER_KEY.toLowerCase(),
];

// Get color scheme for field type
const getFieldColor = (key: string) => {
  const lowerKey = key.toLowerCase();
  if (lowerKey.includes('mpan') || lowerKey.includes('mprn')) {
    return {
      bg: 'bg-[var(--co-brass-wash)] ',
      border: 'border-[var(--co-brass-line)] ',
      icon: 'text-[var(--co-brass-ink)]',
      label: 'text-[var(--co-brass-ink)] ',
    };
  }
  if (lowerKey.includes('eac') || lowerKey.includes('aq')) {
    return {
      bg: 'bg-[var(--co-terracotta-wash)] ',
      border: 'border-[var(--co-terracotta-line)] ',
      icon: 'text-[var(--co-terracotta-ink)]',
      label: 'text-[var(--co-terracotta-ink)] ',
    };
  }
  if (lowerKey.includes('date')) {
    return {
      bg: 'bg-[var(--co-brass-wash)] ',
      border: 'border-[var(--co-brass-line)] ',
      icon: 'text-[var(--co-brass-ink)]',
      label: 'text-[var(--co-brass-ink)] ',
    };
  }
  if (lowerKey.includes('fuel')) {
    return {
      bg: 'bg-[var(--co-green-wash)] ',
      border: 'border-[var(--co-green-line)] ',
      icon: 'text-[var(--co-green-ink)]',
      label: 'text-[var(--co-green-ink)] ',
    };
  }
  return {
    bg: 'bg-gray-50 dark:bg-gray-800/50',
    border: 'border-gray-200 dark:border-gray-700',
    icon: 'text-gray-500 dark:text-gray-400',
    label: 'text-gray-600 dark:text-gray-400',
  };
};

// Enhanced Collapsible Line Item Card Component
interface LineItemCardProps {
  item: LineItem;
  itemIndex: number;
  onUpdateItem: (item: LineItem, data: Partial<LineItem>) => Promise<void>;
  ticketCustomFields?: Record<string, any>;
  fieldNotes?: Record<string, string>; // English rendering / original phrasing, per field
  isQuotationType?: boolean; // Only show quote buttons for Quotation ticket type
  defaultExpanded?: boolean; // Auto-expand when the list is short (1–2 items)
  schemaFields?: SchemaField[]; // When set, show ONLY these (current ticket type's) fields
  ticketTypeId?: string; // Current ticket type — values bind to the type they were entered under
  isNew?: boolean; // This is the create form (no persisted line item yet)
  onDiscard?: () => void; // Cancelling a brand-new draft removes it instead of just collapsing
  onStatusChange?: (item: LineItem, status: 'pending' | 'in_progress' | 'completed') => Promise<void>;
  onDelete?: (item: LineItem) => Promise<void>; // Removes a persisted line item
}

function LineItemCard({ item, itemIndex, onUpdateItem, ticketCustomFields, fieldNotes, isQuotationType = false, defaultExpanded = false, schemaFields, ticketTypeId, isNew = false, onDiscard, onStatusChange, onDelete }: LineItemCardProps) {
  const toastCtx = useToast();
  // A brand-new line item opens expanded and already in edit mode, so the empty
  // fields read unmistakably as a "fill this in to create" form.
  const [isExpanded, setIsExpanded] = useState(isNew || defaultExpanded);
  const [isEditing, setIsEditing] = useState(isNew);
  // Per-field validation errors from the last save attempt (key → message):
  // "Required" for empty required fields, or a format message (URL/date/…).
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Key of the field pending removal (per-field delete in edit mode).
  const [fieldToDelete, setFieldToDelete] = useState<string | null>(null);
  const [isGeneratingQuote, setIsGeneratingQuote] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [confirmingComplete, setConfirmingComplete] = useState(false);
  const [confirmingPending, setConfirmingPending] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editedContent, setEditedContent] = useState(item.content);

  const isCompleted = item.lineItemStatus === 'completed';

  // The status icon toggles the line item between pending and completed, each
  // through a confirmation dialog: pending → mark completed, completed → move
  // back to pending.
  const handleMarkComplete = async () => {
    if (isCompleted || isUpdatingStatus || !onStatusChange) return;
    setIsUpdatingStatus(true);
    try {
      await onStatusChange(item, 'completed');
      setConfirmingComplete(false);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleMarkPending = async () => {
    if (!isCompleted || isUpdatingStatus || !onStatusChange) return;
    setIsUpdatingStatus(true);
    try {
      await onStatusChange(item, 'pending');
      setConfirmingPending(false);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Remove this line item. The parent owns the request + list refresh; the card
  // only drives the confirmation. Keeps the dialog open on failure so the toast
  // and the retry affordance stay together.
  const handleDelete = async () => {
    if (!onDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      await onDelete(item);
      setConfirmingDelete(false);
    } catch {
      // Parent surfaces the error toast.
    } finally {
      setIsDeleting(false);
    }
  };

  const rawDetails = item.lineItemDetails || {};
  // The line item's values belong to the type they were entered under
  // (TYPE_OWNER_KEY). When the selected ticket type differs, the stored values
  // are not this type's — show every field blank (and start edits empty), while
  // keeping the data so switching back to the owning type restores it. Items
  // without an owner stamp (legacy/unsaved) keep their values for every type.
  const ownerTypeId = rawDetails[TYPE_OWNER_KEY] as string | undefined;
  const typeMismatch = Boolean(ownerTypeId && ticketTypeId && ownerTypeId !== ticketTypeId);
  // Effective details drive both display and the edit form's seed values.
  const details = typeMismatch ? {} : rawDetails;

  const [editedDetails, setEditedDetails] = useState<LineItemDetails>({ ...details });
  const [editedDueDate, setEditedDueDate] = useState(item.lineItemDueDate?.split('T')[0] || '');
  const isHiddenKey = (k: string) =>
    k === 'source' || k === 'extracted' || hiddenFields.includes(k.toLowerCase());

  // Index existing detail keys by a normalized form so a schema field (e.g.
  // `broker_email`) resolves to whatever casing the line item stored
  // (`brokerEmail`).
  const normalizeKey = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, '');
  const detailKeyByNorm: Record<string, string> = {};
  for (const k of Object.keys(details)) detailKeyByNorm[normalizeKey(k)] = k;

  // The fields to render. When the ticket type defines a schema, show ONLY that
  // type's fields (filled where a value exists on the line item, empty
  // otherwise) so switching the type re-shapes the visible fields. Without a
  // schema, fall back to whatever keys the line item holds.
  const detailKeys = Object.keys(details).filter((k) => !isHiddenKey(k));
  const displayFields: { key: string; label: string; required: boolean; type?: string }[] =
    schemaFields && schemaFields.length > 0
      ? schemaFields
          .filter((f) => !isHiddenKey(f.key))
          .map((f) => ({
            key: detailKeyByNorm[normalizeKey(f.key)] ?? f.key,
            label: f.name || formatFieldLabel(f.key),
            required: Boolean(f.required),
            type: f.type,
          }))
      : (isEditing ? Object.keys(editedDetails).filter((k) => !isHiddenKey(k)) : detailKeys).map(
          (key) => ({ key, label: formatFieldLabel(key), required: false, type: undefined }),
        );
  // Collapsed preview mirrors the expanded grid: show only the ticket type's
  // defined fields that actually have a value — never raw line-item metadata
  // (from, receivedAt, brokerEmail, source, …) that isn't a configured field.
  const previewFields = displayFields.filter((f) => {
    const v = details[f.key];
    return v !== undefined && v !== null && String(v).trim() !== '';
  });
  const primaryKeys = previewFields.slice(0, 3);

  // Fields that failed validation on the last save attempt — surfaced as a
  // summary banner inside the card (instead of a toast) alongside the inline
  // markers. Each carries its message (Required / invalid URL / invalid date…).
  // The name leads the list — it's the first thing the form asks for, so a
  // blocked save should point there first.
  const errorEntries = [
    ...(fieldErrors[NAME_ERROR_KEY]
      ? [{ label: 'Item Name', message: fieldErrors[NAME_ERROR_KEY] }]
      : []),
    ...displayFields
      .filter((f) => fieldErrors[f.key])
      .map((f) => ({ label: f.label, message: fieldErrors[f.key] })),
  ];

  // Check if quote links exist - check both line item details AND ticket custom fields as fallback
  // Use case-insensitive search to find any field containing quote/quotation + link/url
  const findQuoteLink = (obj: Record<string, any> | undefined): string | undefined => {
    if (!obj) return undefined;
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase().replace(/[_\s]/g, '');
      // Match: quotationlink, quotelink, quotation_link, quote_link, etc.
      if ((lowerKey.includes('quote') || lowerKey.includes('quotation')) &&
          (lowerKey.includes('link') || lowerKey.includes('url'))) {
        if (typeof value === 'string' && value.startsWith('http')) return value;
      }
      // Also check for 'data' field if it's a URL (common API response pattern)
      if (lowerKey === 'data' && typeof value === 'string' && value.startsWith('http')) {
        return value;
      }
    }
    return undefined;
  };
  const findBrokerLink = (obj: Record<string, any> | undefined): string | undefined => {
    if (!obj) return undefined;
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase().replace(/[_\s]/g, '');
      if (lowerKey.includes('broker') && (lowerKey.includes('link') || lowerKey.includes('url') || lowerKey.includes('portal'))) {
        if (typeof value === 'string' && value.startsWith('http')) return value;
      }
    }
    return undefined;
  };

  const quotationLink = findQuoteLink(details) || findQuoteLink(ticketCustomFields);
  const brokerLink = findBrokerLink(details) || findBrokerLink(ticketCustomFields);
  const hasQuoteLinks = brokerLink || quotationLink;
  const horizonStatus = details.horizonStatus || ticketCustomFields?.horizonStatus;
  const canGenerateQuote = !hasQuoteLinks && horizonStatus !== 'generating';

  // Handler for generating quote
  const handleGenerateQuote = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsGeneratingQuote(true);
    try {
      const result = await lineItemsService.generateQuote(item.id);
      if (result.success) {
        toastCtx.success('Quote Generated', 'Links have been added to the line item');
        // Update local state
        setEditedDetails((prev) => ({
          ...prev,
          brokerLink: result.brokerLink,
          quotationLink: result.quotationLink,
          horizonStatus: 'generated',
        }));
        // Trigger refresh in parent
        await onUpdateItem(item, {
          lineItemDetails: {
            ...details,
            brokerLink: result.brokerLink,
            quotationLink: result.quotationLink,
            horizonStatus: 'generated',
          },
        });
      } else {
        toastCtx.error('Quote Generation Failed', result.error || 'Could not generate quote');
      }
    } catch (error: any) {
      toastCtx.error('Error', error.message || 'Failed to generate quote');
    } finally {
      setIsGeneratingQuote(false);
    }
  };

  // Required fields are defined by the ticket type (FieldDefinition.isRequired).
  // Block save until every required field has a non-empty value, flagging the
  // empty ones inline. Applies to create AND edit so the rule can't be bypassed.
  const isEmptyValue = (v: any) => v === undefined || v === null || String(v).trim() === '';
  // Validate required-ness AND format (URL/date/email/number) per field. Empty
  // required → "Required"; a filled field whose value doesn't match its type →
  // a format message. Returns true only when every field passes.
  const validateFields = (): boolean => {
    const errs: Record<string, string> = {};
    // Name is required on create AND edit — the backend enforces the same rule,
    // so letting the form submit a blank one would only produce a 400 toast.
    if (isEmptyValue(editedContent)) {
      errs[NAME_ERROR_KEY] = 'Required';
    }
    for (const f of displayFields) {
      const val = editedDetails[f.key];
      if (f.required && isEmptyValue(val)) {
        errs[f.key] = 'Required';
        continue;
      }
      const kind = getFieldKind(f.key, f.type);
      // Phone checks arrived after line items already held free text (e.g. an
      // AI-extracted "N/A"); an untouched stored value must not block saving
      // an edit to some other field.
      if (kind === 'phone' && val === details[f.key]) continue;
      const msg = validateFieldValue(kind, val == null ? '' : String(val));
      if (msg) errs[f.key] = msg;
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    // Validation errors are surfaced inside the card (summary banner + inline
    // per-field markers), not via a toast.
    if (!validateFields()) return;
    setIsSaving(true);
    try {
      await onUpdateItem(item, {
        // Trimmed to match what the backend stores, so the card doesn't show a
        // name with padding the server has already stripped.
        content: editedContent.trim(),
        lineItemDetails: editedDetails,
        lineItemDueDate: editedDueDate || undefined,
      });
      setIsEditing(false);
    } catch (error) {
      // Error handled in parent
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // For a brand-new draft, cancelling discards it (the parent removes the
    // card) rather than leaving a half-filled, idless card sitting around.
    if (isNew && onDiscard) {
      onDiscard();
      return;
    }
    setEditedContent(item.content);
    setEditedDetails({ ...details });
    setEditedDueDate(item.lineItemDueDate?.split('T')[0] || '');
    setFieldErrors({});
    setIsEditing(false);
  };

  // Clear the name error as the user types; it re-validates on save. Mirrors
  // handleDetailChange so the two inputs behave identically.
  const handleNameChange = (value: string) => {
    setEditedContent(value);
    if (fieldErrors[NAME_ERROR_KEY]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[NAME_ERROR_KEY];
        return next;
      });
    }
  };

  const handleDetailChange = (key: string, value: string) => {
    setEditedDetails((prev) => ({ ...prev, [key]: value }));
    // Clear this field's error as the user edits it; it re-validates on save.
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Re-seed from the type-bound effective details so an edit started after a
    // type switch begins from this type's values (blank on a type mismatch),
    // not whatever was captured at mount.
    setEditedContent(item.content);
    setEditedDetails({ ...details });
    setEditedDueDate(item.lineItemDueDate?.split('T')[0] || '');
    setIsExpanded(true);
    setIsEditing(true);
  };

  return (
    <div className={cn(
      'bg-white dark:bg-gray-800 rounded-lg overflow-hidden transition-all',
      'border',
      isEditing
        ? 'border-[var(--co-brass)] shadow-md'
        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
    )}>
      {/* Compact Header */}
      <div
        className={cn(
          'px-3 py-2.5 cursor-pointer select-none transition-colors',
          isEditing
            ? 'bg-[var(--co-brass-wash)] '
            : 'bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50'
        )}
        onClick={() => !isEditing && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {/* Expand/Collapse Icon */}
          <div className={cn(
            'w-6 h-6 rounded flex items-center justify-center transition-colors flex-shrink-0',
            isExpanded ? 'bg-[var(--co-brass-wash)] ' : 'bg-gray-200 dark:bg-gray-700'
          )}>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-[var(--co-brass-ink)] " />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            )}
          </div>

          {/* Item Number */}
          <div className={cn(
            'w-6 h-6 rounded flex items-center justify-center text-xs font-bold flex-shrink-0',
            isEditing
              ? 'bg-[var(--co-brass)] text-[var(--co-brass-on)]'
              : 'bg-[var(--co-brass-wash)] text-[var(--co-brass-ink)] '
          )}>
            {itemIndex + 1}
          </div>

          {/* Item Name & Preview */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-sm font-medium truncate',
                  isNew && !item.content
                    ? 'text-gray-400 dark:text-gray-500 italic'
                    : 'text-gray-900 dark:text-gray-100',
                )}
              >
                {item.content || (isNew ? 'New line item' : '')}
              </span>
              {isNew && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[var(--co-green-wash)] text-[var(--co-green-ink)] rounded text-xs font-medium flex-shrink-0">
                  New
                </span>
              )}
              {item.lineItemSource?.type === 'email_extraction' && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[var(--co-terracotta-wash)] text-[var(--co-terracotta-ink)] rounded text-xs font-medium flex-shrink-0">
                  <Zap className="w-3 h-3" />
                  AI
                </span>
              )}
            </div>
            {/* Collapsed Preview */}
            {!isExpanded && primaryKeys.length > 0 && (
              <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {primaryKeys.map(({ key, label }) => (
                  <span key={key}>
                    <span className="font-medium">{label}:</span>{' '}
                    <span className="text-gray-700 dark:text-gray-300">{String(details[key])}</span>
                  </span>
                ))}
                {previewFields.length > 3 && (
                  <span className="text-[var(--co-brass-ink)] font-medium">+{previewFields.length - 3} more</span>
                )}
              </div>
            )}
          </div>

          {/* Status icon — kept to the LEFT of the quote/edit actions. A single
              icon reflecting state, clickable BOTH ways via a confirm dialog:
              pending → mark completed, completed → move back to pending. Hidden
              while editing and for the not-yet-saved create form. */}
          {!isEditing && !isNew && (
            <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              {isCompleted ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmingPending(true);
                  }}
                  disabled={isUpdatingStatus}
                  title="Mark as pending"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[var(--co-green-line)] text-[var(--co-green-ink)] text-xs font-medium hover:bg-[var(--co-green-wash)] transition-colors disabled:opacity-50"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Completed
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmingComplete(true);
                  }}
                  disabled={isUpdatingStatus}
                  title="Mark as completed"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[var(--co-terracotta-line)] text-[var(--co-terracotta-ink)] text-xs font-medium hover:bg-[var(--co-terracotta-wash)] transition-colors disabled:opacity-50"
                >
                  <Clock className="w-4 h-4" />
                  Pending
                </button>
              )}
            </div>
          )}

          {/* Quote Links - Only visible for Quotation ticket type */}
          {isQuotationType && !isEditing && quotationLink && (
            <a
              href={quotationLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--co-green)] hover:bg-[var(--co-green)] text-white transition-colors flex-shrink-0"
            >
              <ExternalLink className="w-3 h-3" />
              View Quote
            </a>
          )}

          {isQuotationType && !isEditing && brokerLink && (
            <a
              href={brokerLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary-500 hover:bg-primary-600 text-white transition-colors flex-shrink-0"
            >
              <ExternalLink className="w-3 h-3" />
              Broker Portal
            </a>
          )}

          {/* Generate Quote Button - Only for Quotation type when no quote link exists */}
          {isQuotationType && !isEditing && !quotationLink && canGenerateQuote && (
            <button
              type="button"
              onClick={handleGenerateQuote}
              disabled={isGeneratingQuote}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--co-terracotta)] hover:bg-[var(--co-terracotta)] text-white transition-colors flex-shrink-0 disabled:opacity-50"
            >
              {isGeneratingQuote ? (
                <>
                  <Spinner size="sm" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-3 h-3" />
                  Generate Quote
                </>
              )}
            </button>
          )}

          {/* Edit Button */}
          {!isEditing && (
            <button
              type="button"
              onClick={handleEditClick}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--co-brass)] hover:bg-[var(--co-brass-hover)] text-[var(--co-brass-on)] transition-colors flex-shrink-0"
            >
              <Pencil className="w-3 h-3" />
              {isNew ? 'Add details' : 'Edit'}
            </button>
          )}

          {/* Delete — only for a persisted line item (a not-yet-saved draft is
              discarded via Cancel instead). Any agent/supervisor/admin on the
              ticket may remove one; it soft-deletes and lands in the History
              tab. */}
          {!isEditing && !isNew && onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmingDelete(true);
              }}
              disabled={isDeleting}
              title="Delete line item"
              aria-label="Delete line item"
              className="flex items-center justify-center p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors flex-shrink-0 disabled:opacity-50"
            >
              {isDeleting ? <Spinner size="sm" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          )}

          {/* Save/Cancel in Edit Mode */}
          {isEditing && (
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
              >
                <X className="w-3 h-3" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-[var(--co-green)] hover:bg-[var(--co-green)] text-white transition-colors disabled:opacity-50"
              >
                {isSaving ? <Spinner size="sm" /> : <Save className="w-3 h-3" />}
                {isNew ? 'Create line item' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className={cn(
          'border-t',
          isEditing ? 'border-[var(--co-brass-line)] ' : 'border-gray-200 dark:border-gray-700'
        )}>
          {/* New-item hint — makes it explicit this form CREATES a line item. */}
          {isNew && isEditing && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-[var(--co-green-wash)] border-b border-[var(--co-green-line)] text-xs text-[var(--co-green-ink)] ">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Fill in the fields below, then click{' '}
                <span className="font-semibold">Create line item</span> to add it to this ticket.
                Fields marked <span className="font-semibold">*</span> are required.
              </span>
            </div>
          )}
          {/* Validation summary — shown in the box after a blocked save. */}
          {isEditing && errorEntries.length > 0 && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Please fix the following:{' '}
                <span className="font-semibold">
                  {errorEntries.map((e) => `${e.label} (${e.message})`).join(', ')}
                </span>
              </span>
            </div>
          )}
          {/* Item Name Edit */}
          {isEditing && (
            <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <label
                htmlFor={`line-item-name-${item.id || 'new'}`}
                className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
              >
                Item Name
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                id={`line-item-name-${item.id || 'new'}`}
                type="text"
                value={editedContent}
                onChange={(e) => handleNameChange(e.target.value)}
                aria-invalid={Boolean(fieldErrors[NAME_ERROR_KEY])}
                className={cn(
                  'w-full text-sm px-2.5 py-1.5 border rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 focus:outline-none focus:ring-1',
                  fieldErrors[NAME_ERROR_KEY]
                    ? 'border-red-400 dark:border-red-500 focus:border-red-500 focus:ring-red-500/20'
                    : 'border-gray-300 dark:border-gray-600 focus:border-[var(--co-brass)] focus:ring-[var(--co-brass)]',
                )}
                placeholder="Item name..."
              />
              {fieldErrors[NAME_ERROR_KEY] && (
                <p className="mt-1 text-[10px] text-red-500">{fieldErrors[NAME_ERROR_KEY]}</p>
              )}
            </div>
          )}

          {/* Details Grid */}
          <div className="p-3">
            {displayFields.length > 0 ? (
              <>
              {/* From lg up the record sidebar sits beside this grid, so the
                  column is far narrower than the viewport breakpoint implies. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
                {displayFields.map(({ key, label, required, type }) => {
                  const FieldIcon = getFieldIcon(key);
                  const colors = getFieldColor(key);
                  const value = isEditing ? editedDetails[key] : details[key];
                  const kind = getFieldKind(key, type);
                  const errorMsg = isEditing ? fieldErrors[key] : undefined;
                  const hasError = Boolean(errorMsg);

                  return (
                    <div
                      key={key}
                      className={cn(
                        'rounded-md p-2 border transition-all',
                        colors.bg,
                        hasError
                          ? 'border-red-400 dark:border-red-500'
                          : isEditing
                            ? 'border-gray-300 dark:border-gray-600'
                            : colors.border
                      )}
                    >
                      <div className="flex items-center justify-between gap-1.5 mb-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <FieldIcon className={cn('w-3 h-3 flex-shrink-0', colors.icon)} />
                          <span className={cn('text-[10px] font-semibold uppercase tracking-wide truncate', colors.label)}>
                            {label}
                            {required && <span className="text-red-500 ml-0.5">*</span>}
                          </span>
                        </div>
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => setFieldToDelete(key)}
                            title={`Remove ${label}`}
                            className="flex-shrink-0 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {isEditing ? (
                        <>
                          <input
                            type={htmlInputType(kind)}
                            value={value ?? ''}
                            onChange={(e) =>
                              handleDetailChange(
                                key,
                                kind === 'phone' ? sanitizePhoneInput(e.target.value) : e.target.value,
                              )
                            }
                            className={cn(
                              'w-full px-2 py-1 text-xs border rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 focus:outline-none',
                              hasError
                                ? 'border-red-400 dark:border-red-500 focus:border-red-500'
                                : 'border-gray-300 dark:border-gray-600 focus:border-[var(--co-brass)]'
                            )}
                            placeholder={kind === 'url' ? 'https://…' : kind === 'mpan' ? '13-digit MPAN' : `Enter ${label.toLowerCase()}...`}
                          />
                          {hasError && (
                            <p className="mt-1 text-[10px] text-red-500">{errorMsg}</p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                            {value ?? <span className="text-gray-400 italic font-normal text-xs dark:text-gray-500">Not set</span>}
                          </p>
                          {fieldNotes?.[key] && (
                            // Never replaces the value: the customer's own
                            // words stay above, this reads beneath them.
                            <p
                              className="mt-1 border-l-2 border-gray-300 pl-1.5 text-[11px] leading-snug text-gray-500 dark:border-gray-600 dark:text-gray-400"
                              title={fieldNotes[key]}
                            >
                              {fieldNotes[key]}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}

                {/* Due Date — a built-in line-item property, not a custom
                    field. Only show it when there's NO ticket-type schema
                    driving the display; with a schema we show only the type's
                    defined fields. */}
                {!(schemaFields && schemaFields.length > 0) && (
                  <div className={cn(
                    'rounded-md p-2 border transition-all bg-[var(--co-brass-wash)] ',
                    isEditing ? 'border-gray-300 dark:border-gray-600' : 'border-[var(--co-brass-line)] '
                  )}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Calendar className="w-3 h-3 text-[var(--co-brass-ink)]" />
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--co-brass-ink)] ">
                        Due Date
                      </span>
                    </div>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editedDueDate}
                        onChange={(e) => setEditedDueDate(e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:border-[var(--co-brass)]"
                      />
                    ) : (
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {item.lineItemDueDate
                          ? format(new Date(item.lineItemDueDate), 'MMM d, yyyy')
                          : <span className="text-gray-400 italic font-normal text-xs dark:text-gray-500">Not set</span>
                        }
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Quote Generation Error Message */}
              {horizonStatus === 'failed' && details.horizonError && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-xs text-[var(--co-terracotta-ink)] bg-[var(--co-terracotta-wash)] px-3 py-2 rounded-md">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>Quote generation failed: {details.horizonError}</span>
                  </div>
                </div>
              )}
              </>
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm font-medium">No details available</p>
                <p className="text-xs mt-0.5">Click "Edit" to add information</p>
              </div>
            )}

            {/* AI Info */}
            {item.lineItemSource?.type === 'email_extraction' && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <Zap className="w-3 h-3 text-[var(--co-terracotta-ink)]" />
                  <span>
                    Extracted with{' '}
                    <span className="font-medium text-[var(--co-terracotta-ink)] ">
                      {Math.round((item.lineItemSource.confidence || 0) * 100)}% confidence
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Per-field removal confirm (edit mode) */}
      <ConfirmDialog
        isOpen={fieldToDelete !== null}
        onClose={() => setFieldToDelete(null)}
        onConfirm={() => {
          if (fieldToDelete) {
            setEditedDetails((prev) => {
              const next = { ...prev };
              delete next[fieldToDelete];
              return next;
            });
          }
          setFieldToDelete(null);
        }}
        title="Remove field"
        message={`Remove "${
          fieldToDelete ? formatFieldLabel(fieldToDelete) : ''
        }" from this line item? It's removed when you click Save.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
      />

      {/* Confirm before marking the line item completed */}
      <ConfirmDialog
        isOpen={confirmingComplete}
        onClose={() => setConfirmingComplete(false)}
        onConfirm={handleMarkComplete}
        title="Mark line item as completed?"
        message="This marks the line item completed. You can move it back to pending later."
        confirmLabel="Mark completed"
        cancelLabel="Cancel"
      />

      {/* Confirm before moving a completed line item back to pending */}
      <ConfirmDialog
        isOpen={confirmingPending}
        onClose={() => setConfirmingPending(false)}
        onConfirm={handleMarkPending}
        title="Mark line item as pending?"
        message="This moves the line item back to pending. You can mark it completed again anytime."
        confirmLabel="Mark pending"
        cancelLabel="Cancel"
      />

      {/* Confirm before removing the line item */}
      <ConfirmDialog
        isOpen={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={handleDelete}
        title="Delete line item?"
        message={`This removes "${
          (item.content || '').trim() || `line item ${itemIndex + 1}`
        }" and its details from this ticket. The removal is recorded in the ticket History.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />
    </div>
  );
}

// Collapsible Sidebar Section for Line Items
interface LineItemsSidebarSectionProps {
  ticketId: string;
  ticketCustomFields?: Record<string, any>;
  ticketTypeName?: string; // Used to conditionally show quote buttons (only for "Quotation" type)
}

export function LineItemsSidebarSection({ ticketId, ticketCustomFields, ticketTypeName }: LineItemsSidebarSectionProps) {
  // Check if this is a Quotation ticket type (case-insensitive)
  const isQuotationType = ticketTypeName?.toLowerCase() === 'quotation';
  const toastCtx = useToast();
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [stats, setStats] = useState<LineItemStats | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsData, statsData] = await Promise.all([
        lineItemsService.getLineItems(ticketId),
        lineItemsService.getStats(ticketId),
      ]);
      setLineItems(itemsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load line items:', error);
      toastCtx.error('Error', 'Failed to load line items');
    } finally {
      setLoading(false);
    }
  }, [ticketId, toastCtx]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!loading && lineItems.length === 0) {
    return null;
  }

  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        <div className="flex items-center gap-2">
          <ListTodo className="h-4 w-4 text-[var(--co-brass-ink)]" />
          <span>Line Items</span>
          {stats && stats.total > 0 && (
            <Badge variant="secondary" className="text-xs">
              {stats.total}
            </Badge>
          )}
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="px-4 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner size="sm" />
            </div>
          ) : (
            <div className="space-y-3">
              {lineItems.map((item, index) => {
                const details = item.lineItemDetails || {};
                const detailKeys = Object.keys(details).filter((k) => k !== 'source' && k !== 'extracted');
                // Check both line item details AND ticket custom fields for quote links (case-insensitive)
                const findQuoteLinkInObj = (obj: Record<string, any> | undefined): string | undefined => {
                  if (!obj) return undefined;
                  for (const [key, value] of Object.entries(obj)) {
                    const lowerKey = key.toLowerCase().replace(/[_\s]/g, '');
                    if ((lowerKey.includes('quote') || lowerKey.includes('quotation')) &&
                        (lowerKey.includes('link') || lowerKey.includes('url'))) {
                      if (typeof value === 'string' && value.startsWith('http')) return value;
                    }
                    // Also check for 'data' field if it's a URL
                    if (lowerKey === 'data' && typeof value === 'string' && value.startsWith('http')) {
                      return value;
                    }
                  }
                  return undefined;
                };
                const findBrokerLinkInObj = (obj: Record<string, any> | undefined): string | undefined => {
                  if (!obj) return undefined;
                  for (const [key, value] of Object.entries(obj)) {
                    const lowerKey = key.toLowerCase().replace(/[_\s]/g, '');
                    if (lowerKey.includes('broker') && (lowerKey.includes('link') || lowerKey.includes('url') || lowerKey.includes('portal'))) {
                      if (typeof value === 'string' && value.startsWith('http')) return value;
                    }
                  }
                  return undefined;
                };
                const quotationLink = findQuoteLinkInObj(details) || findQuoteLinkInObj(ticketCustomFields);
                const brokerLink = findBrokerLinkInObj(details) || findBrokerLinkInObj(ticketCustomFields);

                return (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    {/* Compact Header */}
                    <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                      <div className="w-7 h-7 rounded-lg bg-[var(--co-brass-wash)] flex items-center justify-center text-[var(--co-brass-ink)] font-bold text-sm">
                        {index + 1}
                      </div>
                      <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate flex-1">
                        {item.content}
                      </span>
                      {item.lineItemSource?.type === 'email_extraction' && (
                        <Zap className="w-4 h-4 text-[var(--co-terracotta-ink)] flex-shrink-0" />
                      )}
                    </div>

                    {/* Details List */}
                    <div className="px-3 py-2 space-y-1.5">
                      {detailKeys.filter((k) => !hiddenFields.includes(k.toLowerCase())).map((key) => {
                        const colors = getFieldColor(key);
                        return (
                          <div key={key} className="flex items-center gap-2 text-xs">
                            <span className={cn('font-semibold min-w-[80px]', colors.label)}>
                              {formatFieldLabel(key)}:
                            </span>
                            <span className="text-gray-900 dark:text-gray-100 font-medium truncate">
                              {String(details[key] ?? '-')}
                            </span>
                          </div>
                        );
                      })}
                      {item.lineItemDueDate && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-semibold min-w-[80px] text-[var(--co-brass-ink)] ">
                            Due Date:
                          </span>
                          <span className="text-gray-900 dark:text-gray-100 font-medium">
                            {format(new Date(item.lineItemDueDate), 'MMM d, yyyy')}
                          </span>
                        </div>
                      )}

                      {/* Quote Links - Only for Quotation ticket type */}
                      {isQuotationType && (quotationLink || brokerLink) && (
                        <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-2">
                          {quotationLink && (
                            <a
                              href={quotationLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[var(--co-green)] hover:bg-[var(--co-green)] text-white rounded-md text-xs font-medium transition-colors"
                            >
                              <ExternalLink className="w-3 h-3" />
                              View Quote
                            </a>
                          )}
                          {brokerLink && (
                            <a
                              href={brokerLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-xs font-medium transition-colors"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Broker Portal
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              <Button variant="outline" size="sm" onClick={loadData} className="w-full text-xs">
                <RefreshCw className="w-3 h-3 mr-1" />
                Refresh
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Main Component
export function LineItemsTab({ ticketId, compact = false, ticketCustomFields, fieldNotes, ticketTypeName, ticketTypeId }: LineItemsTabProps) {
  const toastCtx = useToast();
  const [loading, setLoading] = useState(true);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [stats, setStats] = useState<LineItemStats | null>(null);
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  // True while the user is adding an extra line item on a ticket that already
  // has one (renders an extra blank "New" card). When the ticket has none yet,
  // the blank create card shows implicitly without this flag.
  const [addingNew, setAddingNew] = useState(false);

  // Check if this is a Quotation ticket type (case-insensitive)
  const isQuotationType = ticketTypeName?.toLowerCase() === 'quotation';

  // Load the current ticket type's custom-field schema so the line item shows
  // ONLY that type's fields. Reloads when the type changes (e.g. via the
  // sidebar), so the visible fields follow the selected type.
  useEffect(() => {
    let cancelled = false;
    if (!ticketTypeId) {
      setSchemaFields([]);
      setSchemaLoading(false);
      return;
    }
    setSchemaLoading(true);
    ticketTypesService
      .getFormSchema(ticketTypeId)
      .then((schema) => {
        if (cancelled) return;
        const fields: SchemaField[] = (schema.fieldGroups ?? []).flatMap((g: any) =>
          (g.fields ?? []).map((f: any) => ({
            key: f.key,
            name: f.name,
            required: Boolean(f.isRequired),
            type: f.fieldType,
          })),
        );
        setSchemaFields(fields);
      })
      .catch(() => {
        if (!cancelled) setSchemaFields([]);
      })
      .finally(() => {
        if (!cancelled) setSchemaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ticketTypeId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsData, statsData] = await Promise.all([
        lineItemsService.getLineItems(ticketId),
        lineItemsService.getStats(ticketId),
      ]);
      setLineItems(itemsData);
      setStats(statsData);
    } catch (error) {
      toastCtx.error('Error', 'Failed to load line items');
    } finally {
      setLoading(false);
    }
  }, [ticketId, toastCtx]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Stamp the line item's values with the current ticket type so they bind to
  // it: when the type later changes, the UI shows these fields blank (the values
  // belong to this type), and restores them when this type is reselected. Only
  // applied when details are being written.
  const stampOwnerType = (data: Partial<LineItem>): Partial<LineItem> => {
    if (!ticketTypeId || !data.lineItemDetails) return data;
    return {
      ...data,
      lineItemDetails: { ...data.lineItemDetails, [TYPE_OWNER_KEY]: ticketTypeId },
    };
  };

  const updateItem = async (item: LineItem, data: Partial<LineItem>) => {
    try {
      const stamped = stampOwnerType(data);
      // An empty id means this is the virtual template shown when the ticket has
      // no line item yet — persist it by CREATING a real line item.
      if (!item.id) {
        await lineItemsService.createLineItem(
          ticketId,
          (stamped.lineItemDetails as Record<string, any>) || {},
          (data.content as string) || undefined,
        );
        toastCtx.success('Saved', 'Line item created');
        setAddingNew(false);
        await loadData();
        return;
      }
      const updated = await lineItemsService.updateLineItem(item.id, stamped);
      // Saving bumped `updatedAt`, so re-sort — the edited item moves to the top
      // rather than waiting for a refresh to jump there.
      setLineItems((prev) => byUpdatedAtDesc(prev.map((li) => (li.id === item.id ? updated : li))));
      toastCtx.success('Saved', 'Line item updated successfully');
    } catch (error) {
      toastCtx.error('Error', 'Failed to save changes');
      throw error;
    }
  };

  const changeStatus = async (
    item: LineItem,
    status: 'pending' | 'in_progress' | 'completed',
  ) => {
    try {
      const updated = await lineItemsService.updateStatus(item.id, status);
      setLineItems((prev) => byUpdatedAtDesc(prev.map((li) => (li.id === item.id ? updated : li))));
      // Keep the pending/completed counts in sync.
      lineItemsService.getStats(ticketId).then(setStats).catch(() => {});
      toastCtx.success('Updated', `Line item marked ${status.replace(/_/g, ' ')}`);
    } catch (error) {
      toastCtx.error('Error', 'Failed to update status');
    }
  };

  const deleteItem = async (item: LineItem) => {
    try {
      await lineItemsService.deleteLineItem(item.id);
      setLineItems((prev) => prev.filter((li) => li.id !== item.id));
      // Counts come from the server, so refresh rather than decrementing —
      // keeps the badge right even if another agent changed things meanwhile.
      lineItemsService.getStats(ticketId).then(setStats).catch(() => {});
      toastCtx.success('Deleted', 'Line item removed');
    } catch (error) {
      toastCtx.error('Error', 'Failed to delete line item');
      throw error;
    }
  };

  if (loading || schemaLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <Spinner size="sm" />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading line items...</p>
        </div>
      </div>
    );
  }

  // Show the create card when: the ticket has no line item yet (implicit, so a
  // typed ticket is never empty), OR the user explicitly clicked "Add line
  // item" to add another. Only ever one blank card at a time.
  const showImplicitTemplate = lineItems.length === 0 && schemaFields.length > 0;

  // What the conversation already collected, for the fields this type defines.
  //
  // A voice or chat conversation collects these answers and they land on the
  // ticket as custom fields. The tab had them all along — as a prop, used only
  // to look for quote links — so an agent opened a ticket whose call had
  // captured nine answers and saw nine empty boxes. The implicit card now opens
  // filled in, and saving it turns them into the line item.
  //
  // Only the current type's fields, so switching type never shows another
  // type's values, and only the implicit card: an explicit "Add line item" is a
  // second item, and seeding that with the first one's values would be wrong.
  const collectedForThisType = (): LineItemDetails => {
    if (!ticketCustomFields || schemaFields.length === 0) return {};
    const norm = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, '');
    const byNorm: Record<string, any> = {};
    for (const [k, v] of Object.entries(ticketCustomFields)) byNorm[norm(k)] = v;

    const seeded: LineItemDetails = {};
    for (const f of schemaFields) {
      const value = byNorm[norm(f.key)];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        seeded[f.key] = value;
      }
    }
    return seeded;
  };

  const emptyTemplate: LineItem = {
    id: '',
    ticketId,
    content: '',
    noteType: 'line_item',
    lineItemDetails: showImplicitTemplate ? collectedForThisType() : {},
    lineItemStatus: 'pending',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  const itemsToRender: LineItem[] = [
    ...lineItems,
    ...(showImplicitTemplate || addingNew ? [emptyTemplate] : []),
  ];

  if (itemsToRender.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
          <FileSearch className="w-8 h-8 text-gray-400 dark:text-gray-500" />
        </div>
        <p className="text-base font-medium text-gray-700 dark:text-gray-300">No Line Items</p>
        <p className="text-sm text-gray-500 mt-1 text-center max-w-sm dark:text-gray-400">
          Line items will appear here when extracted from emails or created manually
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <ListTodo className="w-4 h-4 text-[var(--co-brass-ink)] " />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Line Items
          </h3>
          <Badge variant="secondary" className="text-xs">
            {stats?.total || itemsToRender.length}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Add another line item (only when one already exists and the type
              defines fields — otherwise the blank create card shows on its own). */}
          {schemaFields.length > 0 && lineItems.length > 0 && !addingNew && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddingNew(true)}
              className="h-7 px-2 text-xs text-[var(--co-brass-ink)]"
            >
              <Package className="w-3 h-3 mr-1" />
              Add line item
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={loadData} className="h-7 px-2 text-xs">
            <RefreshCw className="w-3 h-3 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Line Items List —
          auto-expand each card when there are at most 2 items so the user
          sees details immediately. Past that, default to collapsed so the
          list stays scannable. */}
      <div className="space-y-2">
        {itemsToRender.map((item, index) => (
          <LineItemCard
            key={item.id || 'template'}
            item={item}
            itemIndex={index}
            onUpdateItem={updateItem}
            ticketCustomFields={ticketCustomFields}
            fieldNotes={fieldNotes}
            isQuotationType={isQuotationType}
            defaultExpanded={itemsToRender.length <= 2}
            schemaFields={schemaFields}
            ticketTypeId={ticketTypeId}
            isNew={!item.id}
            // Only the explicitly-added draft can be discarded; the implicit
            // create card (ticket has no items yet) always stays available.
            onDiscard={!item.id && lineItems.length > 0 ? () => setAddingNew(false) : undefined}
            onStatusChange={changeStatus}
            // Only persisted items can be deleted; the blank draft has no id.
            onDelete={item.id ? deleteItem : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export default LineItemsTab;
